import React, { useState, useEffect } from 'react';
import { MessageSquare, X, Bug, Flag, Lightbulb, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useToast } from '../hooks/useToast';
import { useAuthStore } from '../store/useAuthStore';
import emailjs from '@emailjs/browser';

type FeedbackType = 'bug' | 'suggestion' | 'other';

// Use this interface until you regenerate database types
interface FeedbackData {
  type: FeedbackType;
  description: string;
  url: string;
  user_agent: string;
  screen_size: string;
  user_id: string | null;
  user_email: string | null;
  created_at: string;
  status: 'new' | 'in_progress' | 'resolved';
}

export default function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState('');
  const { showToast } = useToast();
  const { user } = useAuthStore();

  // EmailJS configuration from environment variables
  const emailjsServiceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
  const emailjsTemplateId = import.meta.env.VITE_EMAILJS_FEEDBACK_TEMPLATE_ID;
  const emailjsPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

  // Initialize EmailJS
  useEffect(() => {
    if (emailjsPublicKey) {
      try {
        emailjs.init(emailjsPublicKey);
      } catch (error) {
        console.warn('Failed to initialize EmailJS:', error);
      }
    }
  }, [emailjsPublicKey]);

  // Reset the form and close the modal
  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setDescription('');
      setFeedbackType('bug');
      setScreenshot(null);
      setIsSubmitted(false);
      setFeedbackId('');
    }, 300);
  };

  const handleScreenshotChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setScreenshot(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description.trim()) {
      showToast({
        type: 'error',
        message: 'Please provide a description of the issue'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get current page URL and other relevant info
      const currentUrl = window.location.href;
      const userAgent = navigator.userAgent;
      const screenSize = `${window.innerWidth}x${window.innerHeight}`;

      // Prepare feedback data
      const feedbackData: FeedbackData = {
        type: feedbackType,
        description: description.trim(),
        url: currentUrl,
        user_agent: userAgent,
        screen_size: screenSize,
        user_id: user?.id || null,
        user_email: user?.email || null,
        created_at: new Date().toISOString(),
        status: 'new',
      };

      // Validate EmailJS configuration before sending
      if (!emailjsServiceId || !emailjsTemplateId || !emailjsPublicKey) {
        console.warn('EmailJS configuration incomplete:', {
          hasServiceId: !!emailjsServiceId,
          hasTemplateId: !!emailjsTemplateId,
          hasPublicKey: !!emailjsPublicKey
        });
      }
      
      // Send email notification to contact@thefuelcentral.com
      try {
        const emailResponse = await emailjs.send(
          emailjsServiceId,
          emailjsTemplateId,
          {
            // Standard EmailJS variables
            from_name: (user as any)?.user_metadata?.full_name || user?.email || 'Anonymous User',
            from_email: user?.email || 'anonymous@example.com',
            to_email: 'contact@thefuelcentral.com',
            subject: `Feedback: ${feedbackType.charAt(0).toUpperCase() + feedbackType.slice(1)}`,
            message: description.trim(),

            // Additional context variables
            feedback_type: feedbackType,
            feedback_description: description.trim(),
            current_url: currentUrl,
            user_agent: userAgent,
            screen_size: screenSize,
            user_email: user?.email || 'Anonymous',
            user_name: (user as any)?.user_metadata?.full_name || user?.email || 'Anonymous User'
          },
          emailjsPublicKey
        );
        
        console.log('Email sent successfully:', emailResponse);
      } catch (emailError: any) {
        console.error('Failed to send email notification:', emailError);
        
        // Provide specific error messages based on EmailJS error codes
        let errorMessage = 'Failed to send email notification';
        if (emailError?.status === 400) {
          errorMessage = 'Invalid email configuration. Please check EmailJS settings.';
        } else if (emailError?.status === 401) {
          errorMessage = 'Email service authentication failed.';
        } else if (emailError?.status === 404) {
          errorMessage = 'Email template not found.';
        }
        
        showToast({
          type: 'error',
          message: `${errorMessage} Your feedback will still be recorded.`
        });
      }

      // Submit to Supabase database
      const { data, error } = await supabase
        .from('feedback')
        .insert(feedbackData)
        .select();
      
      if (error) throw error;

      let submittedFeedbackId = '';
      
      // If screenshot is provided, upload it
      if (screenshot && data && data.length > 0) {
        const feedbackId = data[0].id;
        submittedFeedbackId = feedbackId;

        const fileExt = screenshot.name.split('.').pop();
        // Use user ID in path for RLS policy compliance
        const userId = user?.id || 'anonymous';
        const fileName = `${userId}/${feedbackId}-${Date.now()}.${fileExt}`;

        console.log('Attempting to upload file:', fileName);
        const { error: uploadError } = await supabase
          .storage
          .from('feedback-screenshots')
          .upload(fileName, screenshot);
          
        if (uploadError) {
          console.error('Screenshot upload failed:', uploadError);
          // Don't throw error - feedback is still submitted successfully
        } else {
          // Update feedback record with screenshot URL
          await supabase
            .from('feedback')
            .update({ screenshot_path: fileName })
            .eq('id', feedbackId);
        }
      } else if (data && data.length > 0) {
        submittedFeedbackId = data[0].id;
      }
      
      // Set the actual feedback ID
      setFeedbackId(submittedFeedbackId);
      setIsSubmitted(true);

      showToast({
        type: 'success',
        message: 'Thank you for your feedback! We\'ll review it shortly.'
      });

      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      showToast({
        type: 'error',
        message: 'Failed to submit feedback. Please try again or contact support.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFeedbackTypeIcon = (type: FeedbackType) => {
    switch (type) {
      case 'bug':
        return <Bug className="w-4 h-4" />;
      case 'suggestion':
        return <Lightbulb className="w-4 h-4" />;
      case 'other':
        return <Flag className="w-4 h-4" />;
      default:
        return <Bug className="w-4 h-4" />;
    }
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
        aria-label="Give feedback"
      >
        <MessageSquare className="w-4 h-4" />
        Feedback
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="bg-blue-100 p-2 rounded-full">
                  {isSubmitted ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                <h2 className="text-xl font-semibold">
                  {isSubmitted ? 'Feedback Submitted' : 'Send Feedback'}
                </h2>
              </div>
              <button 
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="mx-auto mb-6 bg-green-100 p-4 rounded-full inline-flex justify-center items-center">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  
                  <h3 className="text-xl font-semibold mb-2">Thank You For Your Feedback!</h3>
                  
                  <p className="text-gray-600 mb-6">
                    Your feedback has been received and will be reviewed by our team. 
                    We appreciate your help in improving The Fuel Central.
                  </p>
                  
                  {feedbackId && (
                    <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-500 mb-6 inline-block">
                      Reference ID: <span className="font-mono">{feedbackId}</span>
                    </div>
                  )}
                  
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-6">
                    <p className="text-amber-800 text-sm">
                      <strong>Beta Notice:</strong> Thank you for helping us test! Your feedback is crucial to improve The Fuel Central.
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What type of feedback do you have?
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {(['bug', 'suggestion', 'other'] as FeedbackType[]).map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => setFeedbackType(type)}
                            className={`flex items-center justify-center gap-2 p-3 rounded-md border ${
                              feedbackType === type
                                ? 'bg-blue-50 border-blue-300 text-blue-700'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {type === 'bug' && <Bug className="w-4 h-4" />}
                            {type === 'suggestion' && <Lightbulb className="w-4 h-4" />}
                            {type === 'other' && <Flag className="w-4 h-4" />}
                            <span className="capitalize">{type}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                        Describe the {feedbackType === 'bug' ? 'issue' : 'feedback'}
                      </label>
                      <textarea
                        id="description"
                        rows={4}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={
                          feedbackType === 'bug' 
                            ? 'Please describe what happened, what you expected, and steps to reproduce...' 
                            : 'Tell us your thoughts...'
                        }
                      ></textarea>
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Attach screenshot (optional)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleScreenshotChange}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-md file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                      {screenshot && (
                        <p className="mt-2 text-sm text-green-600">
                          File selected: {screenshot.name}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="mr-2 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Submit Feedback
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
